import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import resolveImageUrl from "@/lib/imageUrl";
import { EventsList } from "./Events";
import { usePageSEO, SEO } from "@/lib/seo";

// Public /events editorial hub.
// - If the CMS state is "live" and there are published sections, we render
//   the new editorial hub composed of nine section templates.
// - Otherwise we render the legacy `EventsList` so /events never breaks.
//
// The hub reuses existing kind="event" records — Social Run and KB Hallen
// are referenced by slug and never duplicated. Future events created in
// Admin appear automatically in Next Event (auto-picked) and Past Events.

export default function EventsPageRoute() {
  usePageSEO(SEO.events.title, SEO.events.description);
  const { lang } = useI18n();
  const T = (obj) => (obj && (obj[lang] || obj.en || obj.da)) || "";

  const [payload, setPayload] = useState(null);
  const [err, setErr] = useState(null);

  const load = async () => {
    setErr(null);
    const previewMode = new URLSearchParams(window.location.search).get("preview") === "1";
    if (previewMode) {
      try {
        const r = await api.get("/admin/events-page/preview", { withCredentials: true, timeout: 12000 });
        setPayload({ ...r.data, enabled: true });
        return;
      } catch {
        // fall through to the public endpoint
      }
    }
    try {
      const r = await api.get("/events-page", { timeout: 12000 });
      setPayload(r.data);
    } catch (e) {
      setErr(String(e?.message || e));
      // Degrade gracefully: fall back to legacy /events so the route is never stuck.
      setPayload({ state: "draft", sections: [], events: [] });
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  if (err && !payload) {
    return (
      <div className="container-x max-editorial py-24 text-center" data-testid="events-error">
        <div className="label-mono text-terracotta text-[10px]">/events</div>
        <h1 className="mt-3 font-editorial text-3xl">We couldn't load this page right now.</h1>
        <button onClick={load} data-testid="events-retry"
                className="mt-6 inline-flex items-center gap-2 border border-ink text-ink hover:bg-ink hover:text-white px-6 py-3 text-sm tracking-wide">
          Try again
        </button>
      </div>
    );
  }

  if (!payload) {
    return <div className="container-x max-editorial py-24 text-ink/40 text-sm" data-testid="events-loading">Loading…</div>;
  }

  const isLive = (payload.state === "live" && (payload.sections || []).length > 0) || payload.preview;
  if (!isLive) {
    // Legacy fallback — production /events untouched.
    return <EventsList />;
  }
  return <EventsHub payload={payload} T={T} />;
}

function EventsHub({ payload, T }) {
  const sections = payload.sections || [];
  const events = payload.events || [];
  const featured = payload.featured_next_event || null;

  const eventBySlug = useMemo(() => Object.fromEntries(events.map(e => [e.slug, e])), [events]);
  const today = new Date().toISOString().slice(0, 10);
  const pastEvents = useMemo(() => {
    return events
      .filter(e => e.date_iso && e.date_iso < today)
      .sort((a, b) => (b.date_iso || "").localeCompare(a.date_iso || ""));
  }, [events, today]);

  return (
    <div className="bg-base text-ink" data-testid="events-hub">
      {sections.map(s => (
        <SectionRenderer key={s.id} s={s.data} T={T}
                         featured={featured}
                         eventBySlug={eventBySlug}
                         pastEvents={pastEvents} />
      ))}
    </div>
  );
}

function SectionRenderer({ s, T, featured, eventBySlug, pastEvents }) {
  const template = s.template || "editorial_text";
  const props = { s, T, featured, eventBySlug, pastEvents };
  switch (template) {
    case "hero":                 return <HeroBlock {...props} />;
    case "next_event":           return <NextEventBlock {...props} />;
    case "experience_pillars":   return <ExperiencePillars {...props} />;
    case "past_events":          return <PastEvents {...props} />;
    case "social_run_feature":   return <SocialRunFeature {...props} />;
    case "origin":               return <OriginBlock {...props} />;
    case "editorial_text":       return <EditorialText {...props} />;
    case "join":                 return <JoinBlock {...props} />;
    case "final_cta":            return <FinalCTA {...props} />;
    default:                     return <EditorialText {...props} />;
  }
}

const focalPos = (im) => `${im?.focal?.x ?? 50}% ${im?.focal?.y ?? 50}%`;

function Body({ text, className = "text-ink/80" }) {
  if (!text) return null;
  return String(text).replace(/\r\n/g, "\n").split(/\n{2,}/).map(s => s.trim()).filter(Boolean).map((p, i) => (
    <p key={i} className={`mt-4 text-[15px] md:text-[17px] leading-relaxed whitespace-pre-line ${className}`}>{p}</p>
  ));
}

// ─── HERO ──────────────────────────────────────────────────────────────
function HeroBlock({ s, T, featured }) {
  const img = s.images?.[0] || {};
  const scrollTo = (hash) => {
    if (!hash) return;
    const el = document.querySelector(hash);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  // Resolve a safe CTA URL. If the CMS value is not an absolute path, an
  // anchor or a full URL, fall back to the featured next event page so we
  // never emit a dead /events/LIVE route (regression #3).
  const safeUrl = (url) => {
    const u = (url || "").trim();
    if (!u) return featured ? `/events/${featured.slug}` : "";
    if (u.startsWith("/") || u.startsWith("#") || /^https?:\/\//i.test(u)) return u;
    return featured ? `/events/${featured.slug}` : "";
  };
  const cta1 = { label: T(s.cta_label), url: safeUrl(s.cta_url) };
  const cta2 = { label: T(s.cta2_label), url: safeUrl(s.cta2_url) };
  return (
    <section className="relative min-h-[88vh] flex items-end overflow-hidden bg-ink" data-testid="hub-hero">
      {img.url && (
        <img src={resolveImageUrl(img.url)} alt={T(img.alt) || ""} loading="eager"
             className="absolute inset-0 w-full h-full object-cover"
             style={{ objectPosition: focalPos(img) }} />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-ink/95 via-ink/55 to-ink/20" />
      <div className="relative max-editorial container-x pt-32 pb-16 md:pb-24 text-white">
        <div className="label-mono text-terracotta tracking-[0.22em] text-[10px] md:text-[11px]">{T(s.eyebrow)}</div>
        <h1 className="mt-5 font-editorial text-[44px] md:text-[84px] leading-[0.95] tracking-tighter max-w-4xl whitespace-pre-line">{T(s.headline)}</h1>
        <div className="mt-6 max-w-2xl text-white/85"><Body text={T(s.body)} className="text-white/85" /></div>
        <div className="mt-10 flex flex-wrap gap-4">
          {cta1.label && (
            cta1.url?.startsWith("#") ? (
              <button onClick={() => scrollTo(cta1.url)} data-testid="hub-hero-cta1"
                      className="inline-flex items-center gap-2 bg-terracotta hover:bg-terracotta-dark text-white px-8 py-4 text-sm tracking-wide">
                {cta1.label} <ArrowRight size={14} />
              </button>
            ) : (
              <Link to={cta1.url || "/"} data-testid="hub-hero-cta1"
                    className="inline-flex items-center gap-2 bg-terracotta hover:bg-terracotta-dark text-white px-8 py-4 text-sm tracking-wide">
                {cta1.label} <ArrowRight size={14} />
              </Link>
            )
          )}
          {cta2.label && (
            cta2.url?.startsWith("#") ? (
              <button onClick={() => scrollTo(cta2.url)} data-testid="hub-hero-cta2"
                      className="inline-flex items-center gap-2 border border-white/60 text-white hover:bg-white hover:text-ink px-8 py-4 text-sm tracking-wide">
                {cta2.label}
              </button>
            ) : (
              <Link to={cta2.url || "/"} data-testid="hub-hero-cta2"
                    className="inline-flex items-center gap-2 border border-white/60 text-white hover:bg-white hover:text-ink px-8 py-4 text-sm tracking-wide">
                {cta2.label}
              </Link>
            )
          )}
        </div>
      </div>
    </section>
  );
}

// ─── NEXT EVENT ───────────────────────────────────────────────────────
function NextEventBlock({ s, T, featured }) {
  const cover = featured?.cover_image;
  const target = featured ? `/events/${featured.slug}` : "";
  const cta1Url = s.cta_url && s.cta_url.trim() ? s.cta_url : target;
  return (
    <section id="next-event" className="border-t border-line" data-testid="hub-next-event">
      <div className="max-editorial container-x py-24 md:py-32 grid md:grid-cols-12 gap-10 md:gap-16">
        <div className="md:col-span-5">
          <div className="label-mono text-terracotta tracking-[0.22em] text-[10px]">{T(s.eyebrow)}</div>
          {featured && (
            <div className="mt-3 label-mono text-ink/60 tracking-[0.22em] text-[10px]">
              {formatDateShort(featured.date_iso)} · {(featured.venue || "").toUpperCase()} · {(featured.city || "").toUpperCase()}
            </div>
          )}
          <h2 className="mt-4 font-editorial text-[36px] md:text-[64px] leading-[1] tracking-tighter">{T(s.headline)}</h2>
          <div className="mt-6"><Body text={T(s.body)} /></div>
          <div className="mt-10 flex flex-wrap gap-4">
            {T(s.cta_label) && cta1Url && (
              <Link to={cta1Url} data-testid="hub-next-cta1"
                    className="inline-flex items-center gap-2 bg-ink hover:bg-black text-white px-7 py-3.5 text-sm tracking-wide">
                {T(s.cta_label)} <ArrowRight size={14} />
              </Link>
            )}
            {T(s.cta2_label) && s.cta2_url && (
              <Link to={s.cta2_url} data-testid="hub-next-cta2"
                    className="inline-flex items-center gap-2 border border-ink text-ink hover:bg-ink hover:text-white px-7 py-3.5 text-sm tracking-wide">
                {T(s.cta2_label)}
              </Link>
            )}
          </div>
        </div>
        <div className="md:col-span-7 md:col-start-6">
          {cover ? (
            <div className="aspect-[16/11] overflow-hidden bg-line/20">
              <img src={resolveImageUrl(cover)} alt={T(featured?.title) || ""} loading="lazy"
                   className="w-full h-full object-cover hover:scale-[1.02] transition-transform duration-[900ms]" />
            </div>
          ) : (
            <div className="aspect-[16/11] border border-dashed border-line/70 bg-line/10 flex items-center justify-center text-ink/40 label-mono text-[11px]">
              {featured ? T(featured.title) || featured.slug : "No featured event yet"}
            </div>
          )}
          {featured && (
            <div className="mt-6 flex flex-col gap-1">
              <div className="font-editorial text-2xl md:text-3xl leading-tight">{T(featured.title) || featured.slug}</div>
              <div className="label-mono text-ink/60 tracking-widest text-[10px]">
                {formatDateLong(featured.date_iso)}{featured.city ? ` · ${featured.city}` : ""}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

// ─── EXPERIENCE PILLARS ───────────────────────────────────────────────
function ExperiencePillars({ s, T }) {
  const pillars = (s.pillars || []).filter(p => p && p.name);
  return (
    <section className="bg-off-white border-t border-line" style={{ backgroundColor: "#F6F1EA" }} data-testid="hub-pillars">
      <div className="max-editorial container-x py-24 md:py-32">
        <div className="max-w-2xl">
          <div className="label-mono text-terracotta tracking-[0.22em] text-[10px]">{T(s.eyebrow)}</div>
          <h2 className="mt-4 font-editorial text-[36px] md:text-[58px] leading-[1] tracking-tighter">{T(s.headline)}</h2>
          <div className="mt-6"><Body text={T(s.body)} /></div>
        </div>
        <div className="mt-16 grid md:grid-cols-6 gap-8">
          {pillars.map((p, i) => (
            <div key={p.key || i}
                 data-testid={`hub-pillar-${p.key || i}`}
                 className={`${i % 3 === 0 ? "md:col-span-2" : "md:col-span-2"} border-t border-ink/20 pt-6`}>
              <div className="label-mono text-terracotta tracking-widest text-[10px]">{String(i + 1).padStart(2, "0")}</div>
              <div className="mt-2 font-editorial text-2xl md:text-3xl leading-tight">{T(p.name)}</div>
              {T(p.text) && <p className="mt-3 text-sm md:text-base text-ink/70 leading-relaxed">{T(p.text)}</p>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── PAST EVENTS ──────────────────────────────────────────────────────
function PastEvents({ s, T, pastEvents }) {
  const events = (pastEvents || []).slice(0, 12);
  return (
    <section id="past-events" className="border-t border-line" data-testid="hub-past-events">
      <div className="max-editorial container-x py-24 md:py-32">
        <div className="max-w-2xl">
          <div className="label-mono text-terracotta tracking-[0.22em] text-[10px]">{T(s.eyebrow)}</div>
          <h2 className="mt-4 font-editorial text-[36px] md:text-[58px] leading-[1] tracking-tighter">{T(s.headline)}</h2>
          <div className="mt-6"><Body text={T(s.body)} /></div>
        </div>
        {events.length === 0 ? (
          <div className="mt-14 border border-dashed border-line px-6 py-14 text-center text-ink/50 text-sm">
            {T({en: "Past events will appear here as they complete.",
                da: "Forgangne events dukker op her, når de er afholdt.",
                es: "Los eventos pasados aparecerán aquí cuando se completen."})}
          </div>
        ) : (
          <div className="mt-14 grid md:grid-cols-12 gap-x-10 gap-y-16">
            {events.map((e, i) => (
              <Link key={e.slug || i}
                    to={`/events/${e.slug}`}
                    data-testid={`hub-past-${e.slug}`}
                    className={`group ${i % 3 === 0 ? "md:col-span-7" : i % 3 === 1 ? "md:col-span-5" : "md:col-span-6"}`}>
                <div className={`${i % 3 === 0 ? "aspect-[16/10]" : "aspect-[4/5]"} overflow-hidden bg-line/20`}>
                  {e.cover_image ? (
                    <img src={resolveImageUrl(e.cover_image)} alt={T(e.title) || ""} loading="lazy"
                         className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-[900ms]" />
                  ) : null}
                </div>
                <div className="mt-4 flex items-baseline justify-between gap-4">
                  <h3 className="font-editorial text-2xl md:text-3xl leading-tight tracking-tight">{T(e.title) || e.slug}</h3>
                  <span className="label-mono text-ink/50 tracking-widest text-[10px] whitespace-nowrap">{formatDateShort(e.date_iso)}</span>
                </div>
                {T(e.excerpt) && <p className="mt-2 text-sm md:text-base text-ink/70 leading-relaxed">{T(e.excerpt)}</p>}
                <span className="mt-4 inline-flex items-center gap-2 text-[11px] tracking-widest uppercase text-terracotta group-hover:text-terracotta-dark">
                  {T(s.cta_label) || "See what happened"} <ArrowRight size={12} />
                </span>
              </Link>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

// ─── SOCIAL RUN FEATURE ───────────────────────────────────────────────
function SocialRunFeature({ s, T }) {
  const imgs = (s.images || []).filter(i => i && i.url);
  const stats = s.stats || [];
  const ctaUrl = s.cta_url || "/events/social-run-for-spain";
  return (
    <section className="bg-ink text-white" data-testid="hub-social-run">
      <div className="max-editorial container-x py-24 md:py-32 grid md:grid-cols-12 gap-10 md:gap-16">
        <div className="md:col-span-5">
          <div className="label-mono text-terracotta tracking-[0.22em] text-[10px] md:text-[11px]">{T(s.eyebrow)}</div>
          <h2 className="mt-5 font-editorial text-[36px] md:text-[58px] leading-[1] tracking-tighter whitespace-pre-line">{T(s.headline)}</h2>
          <div className="mt-6"><Body text={T(s.body)} className="text-white/80" /></div>
          {stats.length > 0 && (
            <dl className="mt-10 grid grid-cols-3 gap-4 md:gap-6 border-t border-white/15 pt-8">
              {stats.map((st, i) => (
                <div key={i} className="border-r border-white/15 last:border-r-0 pr-3">
                  <dt className="font-editorial text-xl md:text-2xl leading-tight">{st.value}</dt>
                  <dd className="label-mono text-white/60 tracking-widest text-[10px] mt-2">{T(st.label)}</dd>
                </div>
              ))}
            </dl>
          )}
          {T(s.cta_label) && (
            <Link to={ctaUrl} data-testid="hub-social-run-cta"
                  className="mt-10 inline-flex items-center gap-2 text-sm tracking-[0.16em] uppercase border-b border-white/40 pb-1 hover:border-terracotta hover:text-terracotta transition-colors">
              {T(s.cta_label)} <ArrowRight size={14} />
            </Link>
          )}
        </div>
        <div className="md:col-span-7 md:col-start-6">
          {imgs.length > 0 ? (
            <div className="grid grid-cols-6 gap-2 md:gap-3">
              {imgs.slice(0, 4).map((im, i) => (
                <div key={i} className={`overflow-hidden ${i === 0 ? "col-span-6 aspect-[4/3] md:aspect-[16/10]"
                                                      : i === 1 ? "col-span-3 md:col-span-2 aspect-square"
                                                      : i === 2 ? "col-span-3 md:col-span-2 aspect-square"
                                                      : "col-span-6 md:col-span-2 aspect-[4/3]"}`}>
                  <img src={resolveImageUrl(im.url)} alt={T(im.alt) || ""} loading="lazy"
                       style={{ objectPosition: focalPos(im) }}
                       className="w-full h-full object-cover hover:scale-105 transition-transform duration-[900ms]" />
                </div>
              ))}
            </div>
          ) : (
            // Fallback to an existing legacy photograph so the section never
            // shows a "Photos coming" placeholder in production. Admins can
            // still assign curated photos from the CMS.
            <div className="aspect-[16/10] overflow-hidden">
              <img src={resolveImageUrl("/images/paella.jpg")} alt=""
                   loading="lazy"
                   className="w-full h-full object-cover" />
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

// ─── ORIGIN (DANA 2024) ───────────────────────────────────────────────
function OriginBlock({ s, T }) {
  const img = s.images?.[0] || {};
  return (
    <section className="border-t border-line" data-testid="hub-origin">
      <div className="max-editorial container-x py-24 md:py-32 grid md:grid-cols-12 gap-10 md:gap-16">
        <div className="md:col-span-7 md:order-1 order-2">
          {img.url ? (
            <div className="aspect-[4/5] md:aspect-[4/5] overflow-hidden bg-line/20">
              <img src={resolveImageUrl(img.url)} alt={T(img.alt) || ""} loading="lazy"
                   style={{ objectPosition: focalPos(img) }}
                   className="w-full h-full object-cover" />
            </div>
          ) : (
            <div className="aspect-[4/5] border border-dashed border-line bg-line/10" />
          )}
        </div>
        <div className="md:col-span-5 md:order-2 order-1">
          <div className="label-mono text-terracotta tracking-[0.22em] text-[10px]">{T(s.eyebrow)}</div>
          <h2 className="mt-4 font-editorial text-[36px] md:text-[58px] leading-[1] tracking-tighter">{T(s.headline)}</h2>
          <div className="mt-6"><Body text={T(s.body)} /></div>
          {T(s.cta_label) && s.cta_url && (
            <Link to={s.cta_url} data-testid="hub-origin-cta"
                  className="mt-10 inline-flex items-center gap-2 text-sm tracking-[0.16em] uppercase border-b border-ink/40 pb-1 hover:border-terracotta hover:text-terracotta">
              {T(s.cta_label)} <ArrowRight size={14} />
            </Link>
          )}
        </div>
      </div>
    </section>
  );
}

// ─── EDITORIAL TEXT ───────────────────────────────────────────────────
function EditorialText({ s, T }) {
  return (
    <section className="border-t border-line" data-testid={`hub-editorial-${s.slug}`}>
      <div className="max-editorial container-x py-20 md:py-28 max-w-3xl">
        <div className="label-mono text-terracotta tracking-[0.22em] text-[10px]">{T(s.eyebrow)}</div>
        <h2 className="mt-4 font-editorial text-[32px] md:text-[48px] leading-[1.02] tracking-tighter">{T(s.headline)}</h2>
        <div className="mt-6"><Body text={T(s.body)} /></div>
      </div>
    </section>
  );
}

// ─── JOIN ─────────────────────────────────────────────────────────────
function JoinBlock({ s, T }) {
  return (
    <section className="bg-off-white border-t border-line" style={{ backgroundColor: "#F6F1EA" }} data-testid="hub-join">
      <div className="max-editorial container-x py-24 md:py-32 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-7">
          <div className="label-mono text-terracotta tracking-[0.22em] text-[10px]">{T(s.eyebrow)}</div>
          <h2 className="mt-4 font-editorial text-[36px] md:text-[58px] leading-[1] tracking-tighter">{T(s.headline)}</h2>
          <div className="mt-6"><Body text={T(s.body)} /></div>
        </div>
        <div className="md:col-span-5 flex md:flex-col md:justify-end gap-3">
          {T(s.cta_label) && (
            <Link to={s.cta_url || "/join"} data-testid="hub-join-cta1"
                  className="inline-flex items-center justify-center gap-2 bg-terracotta hover:bg-terracotta-dark text-white px-8 py-4 text-sm tracking-wide">
              {T(s.cta_label)} <ArrowRight size={14} />
            </Link>
          )}
          {T(s.cta2_label) && (
            <Link to={s.cta2_url || "/join#volunteer"} data-testid="hub-join-cta2"
                  className="inline-flex items-center justify-center gap-2 border border-ink text-ink hover:bg-ink hover:text-white px-8 py-4 text-sm tracking-wide">
              {T(s.cta2_label)}
            </Link>
          )}
        </div>
      </div>
    </section>
  );
}

// ─── FINAL CTA ────────────────────────────────────────────────────────
function FinalCTA({ s, T, featured }) {
  const img = s.images?.[0] || {};
  const url = (s.cta_url && s.cta_url.trim())
    || (featured ? `/events/${featured.slug}` : "/join");
  return (
    <section className="relative min-h-[70vh] md:min-h-[80vh] flex items-end overflow-hidden bg-ink" data-testid="hub-final">
      {img.url && (
        <img src={resolveImageUrl(img.url)} alt={T(img.alt) || ""} loading="lazy"
             className="absolute inset-0 w-full h-full object-cover"
             style={{ objectPosition: focalPos(img) }} />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/70 to-ink/25" />
      <div className="relative max-editorial container-x py-16 md:py-24 text-white">
        <h2 className="font-editorial text-[40px] md:text-[76px] leading-[0.98] tracking-tighter whitespace-pre-line max-w-4xl">{T(s.headline)}</h2>
        {T(s.body) && <p className="mt-6 text-white/85 text-lg md:text-xl leading-relaxed max-w-2xl">{T(s.body)}</p>}
        {T(s.cta_label) && (
          <Link to={url} data-testid="hub-final-cta"
                className="mt-10 inline-flex items-center gap-2 bg-terracotta hover:bg-terracotta-dark text-white px-8 py-4 text-sm tracking-wide">
            {T(s.cta_label)} <ArrowRight size={14} />
          </Link>
        )}
      </div>
    </section>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────
function formatDateShort(iso) {
  if (!iso) return "";
  try {
    const d = new Date(`${iso}T12:00:00Z`);
    const dd = String(d.getUTCDate()).padStart(2, "0");
    const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
    const yy = String(d.getUTCFullYear());
    return `${dd}.${mm}.${yy}`;
  } catch {
    return iso;
  }
}
function formatDateLong(iso) {
  if (!iso) return "";
  try {
    const d = new Date(`${iso}T12:00:00Z`);
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" });
  } catch {
    return iso;
  }
}
